import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const CMSBrands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    is_enabled: true,
  });
  const [logoFile, setLogoFile] = useState(null);

  const apiBase = process.env.REACT_APP_API_URL || '';
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${apiBase.replace(/\/api\/?$/, '')}${url}`;
  };

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      setLoading(true);
      const res = await api.getEcommerceBrands();
      if (res.success) setBrands(res.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingBrand(null);
    setForm({ name: '', slug: '', description: '', is_enabled: true });
    setLogoFile(null);
    setShowModal(true);
  };

  const handleOpenEdit = (b) => {
    setEditingBrand(b);
    setForm({
      name: b.name,
      slug: b.slug || '',
      description: b.description || '',
      is_enabled: Boolean(b.is_enabled),
    });
    setLogoFile(null);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setLogoFile(file || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingBrand) {
        await api.updateEcommerceBrand(editingBrand.id, form, logoFile);
      } else {
        await api.createEcommerceBrand(form, logoFile);
      }
      setShowModal(false);
      loadBrands();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this brand?')) return;
    try {
      await api.deleteEcommerceBrand(id);
      loadBrands();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-12">
          <button type="button" className="btn btn-primary btn-sm" onClick={handleOpenCreate}>
            <i className="fas fa-plus"></i> New Brand
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Brands</h3>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center p-4">Loading...</div>
          ) : (
            <table className="table table-bordered table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Logo</th>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {brands.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">No brands yet</td>
                  </tr>
                ) : (
                  brands.map((b) => (
                    <tr key={b.id}>
                      <td className="align-middle text-center">
                        {b.logo_url ? (
                          <img src={getImageUrl(b.logo_url)} alt="" style={{ maxWidth: 50, maxHeight: 50, objectFit: 'contain' }} />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="align-middle">{b.name}</td>
                      <td className="align-middle"><code>{b.slug}</code></td>
                      <td className="align-middle">
                        <span className={`badge ${b.is_enabled ? 'bg-success' : 'bg-secondary'}`}>
                          {b.is_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td className="align-middle">
                        <button type="button" className="btn btn-sm btn-info mr-1" onClick={() => handleOpenEdit(b)}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(b.id)}>
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">{editingBrand ? 'Edit Brand' : 'New Brand'}</h4>
                <button type="button" className="close" onClick={() => setShowModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Brand name *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Slug</label>
                    <input
                      type="text"
                      className="form-control"
                      name="slug"
                      value={form.slug}
                      onChange={handleChange}
                    />
                    <small className="form-text text-muted">Leave empty to auto-generate from name</small>
                  </div>
                  <div className="form-group">
                    <label>Logo</label>
                    <input type="file" accept="image/*" className="form-control-file" onChange={handleLogoChange} />
                    {editingBrand && editingBrand.logo_url && !logoFile && (
                      <div className="mt-1">
                        <img src={getImageUrl(editingBrand.logo_url)} alt="" style={{ maxHeight: 40 }} />
                      </div>
                    )}
                    {logoFile && <small className="form-text text-muted">New file selected</small>}
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      rows="2"
                      value={form.description}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <div className="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="brand_enabled"
                        name="is_enabled"
                        checked={form.is_enabled}
                        onChange={handleChange}
                      />
                      <label className="custom-control-label" htmlFor="brand_enabled">Enabled</label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-default btn-sm" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm">
                    {editingBrand ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setShowModal(false)}></div>
        </div>
      )}
    </>
  );
};

export default CMSBrands;
